import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { UtilsService } from './utils.service';

export interface StandardFieldConfig {
  key: string;
  label: string;
  hidden: boolean;
}

export interface CustomFieldConfig {
  key: string;
  label: string;
}

export interface CustomerPageConfig {
  id: number;
  page: 'customer' | 'case';
  customer_type: 'Retail' | 'Corporate';
  standard_fields: StandardFieldConfig[];
  custom_fields_config: CustomFieldConfig[];
}

export const CUSTOMER_SCREEN_MASTER_FIELDS: { key: string; label: string }[] = [
  { key: 'cif_id',                  label: 'CIF ID' },
  { key: 'customer_name',           label: 'Customer Name' },
  { key: 'customer_type',           label: 'Customer Type' },
  { key: 'customer_id',             label: 'Customer ID' },
  { key: 'date_of_birth',           label: 'Date of Birth' },
  { key: 'email',                   label: 'Email' },
  { key: 'customer_onboard_status', label: 'Customer Onboard Status' },
  { key: 'customer_risk_level',     label: 'Customer Risk Level' },
  { key: 'phone_number',            label: 'Phone Number' },
  { key: 'customer_onboarded_at',   label: 'Customer Onboarded At' },
  { key: 'weighted_average',        label: 'Weighted Average' },
  { key: 'country_of_residence',    label: 'Country of Residence' },
  { key: 'regulator',               label: 'Regulator' },
  { key: 'overall_risk',            label: 'Overall Risk' },
  { key: 'place_of_birth',          label: 'Country of Birth' },
  { key: 'modified',                label: 'Last Modified' },
];

export const CUSTOMER_CASE_MASTER_FIELDS: { key: string; label: string }[] = [
  { key: 'cif_id',                  label: 'CIF ID' },
  { key: 'first_name',              label: 'First Name' },
  { key: 'last_name',               label: 'Last Name' },
  { key: 'email',                   label: 'Email' },
  { key: 'phone_number',            label: 'Phone Number' },
  { key: 'customer_type',           label: 'Customer Type' },
  { key: 'customer_onboarded_at',   label: 'Customer Onboarded At' },
  { key: 'risk_profile',            label: 'Risk Profile' },
  { key: 'regulator',               label: 'Regulator' },
  { key: 'citizenship1',            label: 'Citizenship' },
  { key: 'citizenship2',            label: 'Second Citizenship' },
  { key: 'country_name',            label: 'Country' },
  { key: 'country_of_residence',    label: 'Country of Residence' },
  { key: 'place_of_birth',          label: 'Place of Birth' },
  { key: 'year_of_birth',           label: 'Year of Birth' },
  { key: 'city',                    label: 'City' },
  { key: 'address_line_1',          label: 'Address Line 1' },
  { key: 'address_line_2',          label: 'Address Line 2' },
  { key: 'address_type',            label: 'Address Type' },
  { key: 'postal_code',             label: 'Postal Code' },
  { key: 'source_of_wealth',        label: 'Source of Wealth' },
  { key: 'occupation',              label: 'Occupation' },
  { key: 'marital_status',          label: 'Marital Status' },
  { key: 'pep_type',                label: 'PEP Type' },
  { key: 'sanctions_type',          label: 'Sanctions Type' },
  { key: 'blacklist_type',          label: 'Blacklist Type' },
  { key: 'no_of_prior_SAR',         label: 'Prior SARs' },
  { key: 'is_active',               label: 'Active' },
  { key: 'fatca_flag',              label: 'FATCA Flag' },
  { key: 'social_security_number',  label: 'Social Security Number' },
  { key: 'investigative_request',   label: 'Investigative Requests' },
];

@Injectable({ providedIn: 'root' })
export class CustomisationService {
  private httpClient = inject(HttpClient);
  private utilsService = inject(UtilsService);
  private cache = new Map<string, CustomerPageConfig>();

  getAllConfigs(): Observable<CustomerPageConfig[]> {
    return this.httpClient.get<CustomerPageConfig[]>(
      this.utilsService.buildApiUrl('customisation/page-config/')
    );
  }

  getPageConfig(
    page: 'customer' | 'case',
    customerType: 'Retail' | 'Corporate'
  ): Observable<CustomerPageConfig | null> {
    const key = `${page}:${customerType}`;
    const cached = this.cache.get(key);
    if (cached) return of(cached);

    return this.httpClient
      .get<CustomerPageConfig[]>(
        this.utilsService.buildApiUrl(
          `customisation/page-config/?page=${page}&customer_type=${customerType}`
        )
      )
      .pipe(
        map(configs => configs[0] ?? null),
        tap(config => { if (config) this.cache.set(key, config); })
      );
  }

  updatePageConfig(
    id: number,
    payload: Pick<CustomerPageConfig, 'standard_fields' | 'custom_fields_config'>
  ): Observable<CustomerPageConfig> {
    return this.httpClient
      .patch<CustomerPageConfig>(
        this.utilsService.buildApiUrl(`customisation/page-config/${id}/`),
        payload
      )
      .pipe(
        tap(config => this.cache.set(`${config.page}:${config.customer_type}`, config))
      );
  }

  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Merges a saved config's standard_fields onto the master field list,
   * respecting the saved order. Fields not yet in the saved config are
   * appended at the end with hidden=false.
   */
  applyStandardFieldConfig(
    masterFields: { key: string; label: string }[],
    savedFields: StandardFieldConfig[]
  ): StandardFieldConfig[] {
    const masterMap = new Map(masterFields.map(f => [f.key, f]));
    const savedMap = new Map(savedFields.map(f => [f.key, f]));

    // Saved fields first (in saved order), then any master fields not yet saved
    const ordered: StandardFieldConfig[] = [
      ...savedFields
        .filter(f => masterMap.has(f.key))
        .map(f => ({ key: f.key, label: masterMap.get(f.key)!.label, hidden: f.hidden })),
      ...masterFields
        .filter(m => !savedMap.has(m.key))
        .map(m => ({ key: m.key, label: m.label, hidden: false })),
    ];
    return ordered;
  }
}
